import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** Event name indicating successful document signing completion */
const SigningCompleteEvent = "signing_complete";

/** Array of event names that indicate errors during the signing process */
const RedirectErrorEvents = Object.freeze(["cancel", "decline", "exception", "session_timeout", "ttl_expired"]);

/**
 * Custom React hook that handles DocuSign redirect effects and updates interview data
 * based on document signing outcomes.
 * 
 * This hook monitors URL search parameters for DocuSign redirect events and tracks
 * the signing status. The actual interview updates are deferred to the Submit action
 * to prevent the interview from becoming Complete before the user explicitly submits.
 * 
 * @returns {{documentsWereSigned: boolean, errorSigningDocuments: boolean}} Object containing signing status
 * @returns {boolean} returns.documentsWereSigned - True if documents were successfully signed in this session
 * @returns {boolean} returns.errorSigningDocuments - True if there was an error during signing
 */
export default function useDocusignReturnEffects() {
    const [urlSearchParams] = useSearchParams();

    /** State to track if documents were successfully signed */
    const [documentsWereSigned, setDocumentsWereSigned] = useState(false);

    /** State to track if there was an error during document signing */
    const [errorSigningDocuments, setErrorSigningDocuments] = useState(false);

    useEffect(() => {
        /** Get the DocuSign redirect event from URL parameters */
        const redirectEvent = urlSearchParams.get("event");

        // No need to process if this render cycle is not a redirect from DocuSign
        if (!redirectEvent || redirectEvent === "") {
            return;
        }

        /** Check if the redirect event indicates successful signing */
        const eventIsSigningComplete = redirectEvent === SigningCompleteEvent;

        /** Check if the redirect event indicates an error occurred */
        const eventIsError = RedirectErrorEvents.includes(redirectEvent);

        setDocumentsWereSigned(eventIsSigningComplete);
        setErrorSigningDocuments(eventIsError);

        // Note: We intentionally do NOT update interview flags here.
        // The signed document flags (HasSignedFCRA, HasSignedACH, etc.) are now
        // updated only when the user clicks Submit in useFormEvents.js.
        // This prevents the interview from becoming Complete before explicit submission.
    }, [urlSearchParams]);

    return {
        documentsWereSigned,
        errorSigningDocuments
    };
}